"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export type Currency = "sek" | "eur";

const SESSION_KEY = "dovie_user_country";
const IPAPI_URL = "https://ipapi.co/json/";

/**
 * Detects the user's currency based on their geographic location.
 * Uses ipapi.co for geo-IP detection (primary).
 * Falls back to language selection if geo-IP is unavailable.
 *
 * Logic:
 *  - Sweden (SE) → SEK
 *  - All other countries → EUR
 *  - Caches country code in sessionStorage for the session duration
 */
export function useCurrency() {
  const { i18n } = useTranslation();
  const [currency, setCurrency] = useState<Currency>("sek");
  const [isLoading, setIsLoading] = useState(true);
  const [countryCode, setCountryCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const detectCurrency = async () => {
      // 1. Check sessionStorage cache first
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        if (!cancelled) {
          setCountryCode(cached);
          setCurrency(cached === "SE" ? "sek" : "eur");
          setIsLoading(false);
        }
        return;
      }

      // 2. Fetch from ipapi.co
      try {
        const response = await fetch(IPAPI_URL, { signal: AbortSignal.timeout(4000) });
        if (!response.ok) throw new Error("ipapi.co request failed");

        const data = await response.json();
        const country: string = data.country_code || "";

        sessionStorage.setItem(SESSION_KEY, country);

        if (!cancelled) {
          setCountryCode(country);
          setCurrency(country === "SE" ? "sek" : "eur");
          setIsLoading(false);
        }
      } catch {
        // 3. Fallback: derive from language
        if (!cancelled) {
          const langCurrency = i18n.language === "sv" ? "sek" : "eur";
          setCurrency(langCurrency);
          setIsLoading(false);
        }
      }
    };

    detectCurrency();

    return () => {
      cancelled = true;
    };
    // Only run once on mount — country doesn't change during a session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Secondary signal: if no country cache exists yet, also react to language changes
  useEffect(() => {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (!cached && !isLoading) {
      setCurrency(i18n.language === "sv" ? "sek" : "eur");
    }
  }, [i18n.language, isLoading]);

  return {
    currency,
    isSweden: currency === "sek",
    isLoading,
    countryCode,
  };
}
